import React, { useEffect, useRef, useState } from 'react';
import {
  DOMAIN_PREFIX,
  LANGUAGE_ENUMS,
  LANGUAGE_VALUE,
  TLANGUAGE_VALUE,
  domainCode,
} from '../../../constants/staticPageConstants';
import { Meta } from '../../../layout/Meta';
import { DateFormatter } from '../../../utils/date-utils';
import { processURL } from '../../../utils/static-pages/urlProcessor';
import { handleTrack } from '../../../utils/tracker';
import { Breadcrumb } from '../../Atoms/BreadCrumb';
import { ButtonVariants } from '../../Atoms/Button';
import { If } from '../../Atoms/Conditionals';
import { DemoModal } from '../../Atoms/DemoModal';
import { MarktechModal } from '../../Atoms/Modal';
import { AuthorCard } from '../../Author/AuthorCard';
import { ALIGN } from '../../Author/SocialMedia';
import { StickyCTA } from '../../CTA/StickyCTA';
import { FILE_NOW_TEXT } from '../../CTA/constants';
import {
  REQUEST_DEMO_LINK,
  demoText,
  showBookDemoPopUp,
} from '../../CTA/utils';
import { FaqComponent } from '../../Common/FAQS';
import { checkIfB2CPage } from '../../Header/utils';
import { CloseIcon } from '../../Icons';
import { MobileTableOfContent, TableOfContent } from './TOC';
import { KSA_LHS, LeftSideArticles } from './articles/leftArticleList';
import {
  AuthorDetails,
  CMSHeader,
  CMSLayoutSchema,
  H1Heading,
  NAV_TYPES,
  TopCTA,
} from './components';
import { useOnMountCMSLayout } from './hooks/useOnMountCMSLayout';
import { LANG_DATA } from './language/dataset';
import { LanguageSwitch } from './language/languageSwitch';
import { generateLanguageTags } from './language/languageTags';
import { BreadCrumbLayout, MainLayout, PageWrapper } from './styles';
import { IFAQ, ISINGLE_POST } from './types';
import {
  ReusableComponent,
  ShareIcons,
  createTOC,
  decodeHtml,
  getActiveFAQsInFAQFormat,
  getBreadCrumbData,
  optimiseYoutube,
  stripUnwantedStyles,
} from './util';

interface ICMS_LAYOUT_PROPS {
  data: ISINGLE_POST;
  modifiedDate: string;
}

export const CMSLayout = (params: ICMS_LAYOUT_PROPS): JSX.Element => {
  const { modifiedDate, data } = params;

  const [displayPopup, setDemoPopup] = useState(false);
  const [TocDom, setTOC] = useState<HTMLDivElement | null>(null);

  const myContainer = useRef(null);
  const modalRefCTA = useRef(null);

  const isB2CPage = checkIfB2CPage(data.category.slug);
  const LANGUAGE = LANGUAGE_VALUE[
    data?.language as TLANGUAGE_VALUE
  ] as LANGUAGE_ENUMS;
  const pageURL = processURL(data.permalink, LANGUAGE, data.domain);
  let CURR_PAGE_URL = `https://www.${
    DOMAIN_PREFIX[data.domain]?.host
  }/${pageURL}`;

  if (data.domain === domainCode.cleartaxIn) {
    CURR_PAGE_URL = `https://${DOMAIN_PREFIX[data.domain]?.host}/${pageURL}`;
  }

  const authorDetails = data.author_details;
  const showAuthor =
    authorDetails.status === true && authorDetails.domain.includes(data.domain);

  useEffect(() => {
    setTOC(createTOC(myContainer.current));
    data.content = stripUnwantedStyles(data.content);
    if (data.faq) {
      data.faq?.forEach((item, index) => {
        if (data && data.faq && data.faq[index] && data.faq[index]?.answer) {
          data.faq[index] = {
            ...data.faq[index],
            answer: stripUnwantedStyles(item.answer),
          } as IFAQ;
        }
      });
    }
    optimiseYoutube(myContainer.current);
    ReusableComponent(myContainer.current);
  }, [myContainer.current]);

  useOnMountCMSLayout(data);

  const BREADCRUMB_DATA = getBreadCrumbData(data, LANGUAGE);

  const captureEvent = (e: React.MouseEvent<Element>) => {
    if (!(e?.target instanceof HTMLElement)) {
      return;
    }
    const isLabelRequestADemo = e?.target.dataset.label;

    if (isLabelRequestADemo && isLabelRequestADemo === 'Request a demo') {
      e.preventDefault();
      setDemoPopup(true);
    }
    handleTrack({
      label: 'top_cta',
      obj: e.target,
    });
  };
  const stickyCTAProp = isB2CPage
    ? { type: NAV_TYPES.B2C }
    : { link: REQUEST_DEMO_LINK(data.domain) };

  return (
    <>
      <If condition={data.domain !== domainCode.cleartaxSA}>
        <StickyCTA
          buttonText={isB2CPage ? FILE_NOW_TEXT : demoText(data.domain)}
          buttonStyle={ButtonVariants.bluefilled}
          {...stickyCTAProp}
        />
      </If>
      <If condition={showBookDemoPopUp(data.domain)}>
        <div onClick={() => setDemoPopup(true)}>
          <StickyCTA
            buttonText={isB2CPage ? FILE_NOW_TEXT : demoText(data.domain)}
            buttonStyle={ButtonVariants.bluefilled}
            {...stickyCTAProp}
          />
        </div>
      </If>
      <MarktechModal
        open={displayPopup}
        onClose={() => setDemoPopup(false)}
        classNames={{
          modal:
            'md:w-11/12 sm:w-11/12 h-fit max-h-[90vh] overflow-hidden w-1/2 mobile-search-box rounded-2xl',
        }}
        closeIcon={<CloseIcon />}
        center
      >
        <DemoModal
          modalRef={modalRefCTA}
          demoPopup={setDemoPopup}
          language={LANGUAGE}
          domain={data.domain}
        />
      </MarktechModal>
      <CMSHeader
        domain={data.domain}
        category={data.category}
        language={LANGUAGE}
        isMultiLang={data.multi_language}
        permalink={data.permalink}
      />
      <Meta
        title={decodeHtml(data.meta_title).replace('&', 'and')}
        description={data.description}
        ampURL={
          pageURL === 'budget-2023-highlights' ? `${CURR_PAGE_URL}-amp` : null
        }
        canonical={CURR_PAGE_URL}
        favicon={`${
          data.domain === domainCode.ClearTech
            ? 'https://assets1.cleartax-cdn.com/cleartax/images/1675852730_image5.png'
            : 'https://assets1.cleartax-cdn.com/cleartax/images/1644906212_clearfavicon.png'
        } `}
        hrefLang={generateLanguageTags(data.multi_language, data)}
      ></Meta>
      <PageWrapper theme={{ direction: data.language === 3 ? 'rtl' : 'ltr' }}>
        <TopCTA CTA={data.cta || ''} onClickCB={captureEvent} />
        <div
          className={` ${
            data.domain === domainCode.ClearTech ? 'mt-8' : 'mt-6'
          }  flex sm:flex-col-reverse `}
          ref={myContainer}
          data-testid="cms-page"
        >
          <div className="w-1/5 sm:w-full">
            {data.domain === domainCode.cleartaxIn ||
            data.domain === domainCode.ClearTech ? (
              <LeftSideArticles
                relatedPost={data.related_posts}
                popularPost={data.popular_posts}
                interestedPost={data.interesting_posts}
                language={data.language}
                domainName={DOMAIN_PREFIX[data.domain]?.domain || 'cleartax.in'}
              />
            ) : (
              <>
                <KSA_LHS domain={data.domain} language={data.language} />
                <LeftSideArticles
                  relatedPost={data.related_posts}
                  popularPost={data.popular_posts}
                  interestedPost={data.interesting_posts}
                  language={data.language}
                  domainName={DOMAIN_PREFIX[data.domain]?.domain || ''}
                />
              </>
            )}
          </div>

          <div className="w-3/5 sm:w-full sm:px-4">
            <BreadCrumbLayout className="bg-white pt-10">
              <Breadcrumb dataSet={BREADCRUMB_DATA} />
            </BreadCrumbLayout>
            <H1Heading title={data.title} />
            <div className="flex mb-4  sm:flex-col sm:mb-2 items-center md:flex-wrap">
              <div className="flex w-full whitespace-pre sm:flex-col">
                <If condition={showAuthor}>
                  <AuthorDetails name={authorDetails.name} />
                </If>

                <div className="flex">
                  <p className="text-s-14 py-2 w-full sm:py-0 self-center sm:my-1  sm:w-full">
                    {LANG_DATA[LANGUAGE]?.captionItem.updatedOn}:{' '}
                    {DateFormatter(modifiedDate).format('MMM Do, YYYY')}
                  </p>

                  <p className="self-center"> | </p>
                  <p className="text-s-14 py-2  self-center w-full sm:py-0 sm:mx-0 sm:my-2   sm:w-full">
                    {Math.ceil(data.content.length / 1500)}{' '}
                    {LANG_DATA[LANGUAGE]?.captionItem.minRead}
                  </p>
                </div>
              </div>
              <LanguageSwitch
                className="text-s-14 text-gray-500"
                isMultiLanguage={data.multi_language}
                pageData={data}
              />
              <ShareIcons
                url={data.permalink}
                domain={data.domain}
                language={LANGUAGE}
                align={ALIGN.right}
              />
            </div>

            <MainLayout>
              <MobileTableOfContent content={TocDom} language={LANGUAGE} />
              <div dangerouslySetInnerHTML={{ __html: data.content }} />

              <FaqComponent
                FAQS={getActiveFAQsInFAQFormat(data.faq)}
                isShowSchema={true}
                variant="partner"
                faqIconColor={'#314259'}
                htmlFromApi={true}
                expanded={'false'}
              />
            </MainLayout>

            <If condition={showAuthor && !!authorDetails?.description}>
              <div id="authorBio" className="pt-14">
                <AuthorCard
                  name={authorDetails.name}
                  designation={authorDetails.designation}
                  authorImg={authorDetails.image_url || ''}
                  description={authorDetails.description}
                  linkedin={authorDetails.linkedin_url}
                  facebook={authorDetails.facebook_url}
                  twitter={authorDetails.twitter_url}
                  slug={authorDetails.slug}
                  showHeading={true}
                />
              </div>
            </If>
          </div>
          <div className="w-1/5 sm:w-full sm:px-4">
            <TableOfContent content={TocDom} language={LANGUAGE} />
          </div>
        </div>
      </PageWrapper>
      <CMSLayoutSchema
        postData={data}
        pageUrl={CURR_PAGE_URL}
        language={LANGUAGE}
      />
    </>
  );
};
